import { FC, useCallback, useEffect, useState } from 'react'
import PendingProposalsContent from '../components/management/proposalReview/pendingProposals/PendingProposalsContent'
import { ProposalQuoteOption } from '@/types'
import apiClient from '../services/api'

const formatDateTime = (isoString: string): string => {
  if (!isoString) return ''

  const date = new Date(isoString)
  return date
    .toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true, // Cambiado de false a true para formato AM/PM
      timeZone: 'America/Mexico_City',
    })
    .replace(',', '') // Quitar la coma entre fecha y hora
    .replace('a.m.', 'AM') // Reemplazar "a. m." por "AM"
    .replace('p.m.', 'PM') // Reemplazar "p. m." por "PM"
}

// Mapeo compartido de la respuesta de /cotizador/mis-cotizaciones-prospecto
// a ProposalQuoteOption[] — antes estaba duplicado (casi idéntico) entre el
// fetch de montaje y refreshProposals; se centraliza aquí para ambos casos
// y también para el fetch de vencidas.
const mapProposalsResponse = (data: any[]): ProposalQuoteOption[] => {
  return data.map((item: any) => {
    const cot = item.cotizacion
    return {
      id: cot.id,
      vehicleData: {
        marca: cot.request_data.brand,
        modelo: cot.request_data.year,
        valorFactura: String(cot.request_data.valor_factura),
        submarcaVersion: cot.request_data.subBrand || '',
        tipo: cot.request_data.tipo || '',
        costoAccesorios: String(cot.request_data.accesorios || ''),
        descripcionAccesorios: cot.request_data.descripcion_accesorios || '',
        periodoCotizacion: String(cot.request_data.plazo_meses || ''),
      },
      proposalGeneralInfo: {
        fecha: new Date(cot.created_at).toISOString().split('T')[0],
        nombreCliente: cot.sellersCustomer?.company || '',
        atencionA: cot.sellersCustomer?.contact || '',
        tipoDeActivo: cot.request_data.tipo_activo || '',
        numeroCotizaciones: 1,
      },
      adjustments: {
        leaseTerm: Number(cot.request_data.plazo_meses || 0),
        advancePayment: Number(cot.request_data.pago_inicial_total || 0),
        residualValue: Number(cot.request_data.residual_siva || 0),
        guaranteeDeposit: Number(cot.request_data.deposito_garantia || 0),
      },
      conditions: {
        leaseAmount: String(cot.request_data.valor_factura || ''),
        insurance: String(cot.request_data.seguro || ''),
        totalInitialAmount: String(cot.request_data.pago_inicial_total || ''),
        openingFee: String(cot.request_data.tasa_comision_apertura || ''),
        residualValueNoTax: String(cot.request_data.residual_siva || ''),
        monthlyRentNoTax: '',
        monthlyDiscount: '',
        totalRentWithDiscount: '',
        totalRentWithTax: '',
        rentTax: '',
        reserveFund: String(cot.request_data.tasa_fondo_reserva || ''),
      },
      status: item.estatus,
      created_at: new Date(cot.created_at).toISOString().split('T')[0],
      startTime: formatDateTime(item.startTime),
      guarantorEnabled: item.guarantorEnabled || cot.guarantorEnabled,
      guarantorUserId: Object.prototype.hasOwnProperty.call(
        item,
        'guarantorUserId'
      )
        ? item.guarantorUserId
        : cot.guarantorUserId,
      creditApplicationId:
        item.creditApplicationId || cot.creditApplicationId || item.id,
    }
  })
}

// GET compartido — `soloExpiradas` es el parámetro opcional que backend
// agregó para traer únicamente las cotizaciones vencidas.
const fetchMisCotizacionesProspecto = async (
  soloExpiradas?: boolean
): Promise<ProposalQuoteOption[]> => {
  const token = localStorage.getItem('auth_token')
  const res = await apiClient.get('/cotizador/mis-cotizaciones-prospecto', {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
    params: soloExpiradas ? { soloExpiradas: true } : undefined,
  })
  const data = await res.data
  if (data.success && Array.isArray(data.data)) {
    return mapProposalsResponse(data.data)
  }
  return []
}

const PendingProposalsRoute: FC = () => {
  const [proposals, setProposals] = useState<ProposalQuoteOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        setProposals(await fetchMisCotizacionesProspecto())
      } catch {
        setProposals([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const refreshProposals = () => {
    setLoading(true)
    const load = async () => {
      try {
        setProposals(await fetchMisCotizacionesProspecto())
      } catch {
        setProposals([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }

  // Refresco "silencioso" (sin pasar por el loader de página completa) —
  // se usa al regresar a la tab "Activas" desde "Vencidas", donde
  // PendingProposalsContent debe seguir montado (para no perder su
  // activeTab ni el resto de su estado) mientras se traen datos frescos.
  // useCallback con deps vacías: PendingProposalsContent depende de esta
  // referencia en un useEffect (para volver a pedir datos al cambiar de
  // tab) — si se recreara en cada render, ese efecto se dispararía en
  // loop (cada llamada aquí hace setProposals → re-render → nueva
  // referencia → el efecto se vuelve a disparar → nueva llamada...).
  const refreshProposalsSilently = useCallback(async () => {
    try {
      setProposals(await fetchMisCotizacionesProspecto())
    } catch {
      // Si falla, se deja la lista tal como estaba.
    }
  }, [])

  // Trae las cotizaciones vencidas bajo demanda (se llama la primera vez
  // que el usuario abre la tab "Vencidas" en PendingProposalsContent).
  // Mismo motivo de useCallback que refreshProposalsSilently.
  const loadVencidas = useCallback(() => fetchMisCotizacionesProspecto(true), [])

  if (loading)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="p-8 text-center">Cargando cotizaciones...</div>
      </div>
    )

  return (
    <PendingProposalsContent
      proposals={proposals}
      onRefresh={refreshProposals}
      onRefreshActivas={refreshProposalsSilently}
      onLoadVencidas={loadVencidas}
    />
  )
}

export default PendingProposalsRoute
